import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Tooltip,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { GridInAccordion } from "./Components";
import { useTheme } from "@mui/material/styles";

const EnhancedAccordion = ({
  title,
  images,
  selectedImages,
  lockedSelectedImages,
  onImageSelect,
  gridColumns,
  dimensions,
  itemSize,
  backgroundColor = "#2a2a2a",
  onExpand,
  subfolders = null,
  containerWidth,
  selectsMade,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (event, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded) {
      const imagesNeedingLinks = images.filter((img) => !img.thumbnailUrl);
      if (imagesNeedingLinks.length > 0) {
        onExpand(imagesNeedingLinks, title);
      }

      if (subfolders && typeof subfolders === "object") {
        // Object.entries(subfolders).forEach(([subfolderName, subfolder]) => {
        //   const subfolderImagesNeedingLinks = subfolder.filter(
        //     (img) => !img.thumbnailUrl
        //   );
        //   if (subfolderImagesNeedingLinks.length > 0) {
        //     onExpand(subfolderImagesNeedingLinks, `${title}/${subfolderName}`);
        //   }
        // });
      }
    }
  };

  const allImages = React.useMemo(() => {
    const subfolderImages = [];
    // const subfolderImages = subfolders && typeof subfolders === 'object' ? Object.values(subfolders).flat() : [];
    return [...images, ...subfolderImages];
  }, [images, subfolders]);

  const aggregatedCounts = React.useMemo(() => {
    const total = allImages.length;
    const selected = allImages.filter((img) =>
      selectedImages.some((sel) => sel.name === img.name)
    ).length;
    return { total, selected };
  }, [allImages, selectedImages]);

  const handleResetClick = (e) => {
    e.stopPropagation();
    const selectedInAccordion = allImages.filter((img) =>
      selectedImages.some((sel) => sel.name === img.name)
    );
    if (selectedInAccordion.length > 0) {
      onImageSelect(selectedInAccordion, true);
    }
  };

  const updatedDimensions = {
    ...dimensions,
    width: containerWidth || dimensions.width,
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      TransitionProps={{
        unmountOnExit: true,
        timeout: 150,
      }}
      sx={{
        background:
          theme.palette.mode === "light" ? "#ffffff" : backgroundColor,
        color: theme.palette.mode === "light" ? "#000" : "#fff",
        marginBottom: "12px",
        borderRadius: "8px !important",
        overflow: "hidden",
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(0,0,0,0.1)"
            : "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        transition: "border-color 0.2s ease",
        "&:hover": {
          borderColor:
            theme.palette.mode === "light"
              ? "rgba(0,0,0,0.2)"
              : "rgba(255,255,255,0.2)",
        },
        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            sx={{
              color: theme.palette.mode === "light" ? "#000" : "#fff",
              transition: "transform 0.2s ease",
            }}
          />
        }
        sx={{
          "& .MuiAccordionSummary-content": {
            minWidth: 0,
            overflow: "hidden",
            margin: "16px 0",
          },
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "light"
                ? "rgba(0,0,0,0.02)"
                : "rgba(255,255,255,0.02)",
          },
          transition: "background-color 0.2s ease",
          borderRadius: "4px",
        }}
      >
        <Tooltip title={title} placement="top">
          <Typography
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: "default",
              fontWeight: 600,
              fontSize: "1rem",
              letterSpacing: "-0.01em",
              alignContent: "center",
            }}
          >
            {title}
          </Typography>
        </Tooltip>

        <Box
          sx={{
            flexShrink: 0,
            ml: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {!selectsMade && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleResetClick}
              sx={{
                visibility: allImages.some((img) =>
                  selectedImages.some((sel) => sel.name === img.name)
                )
                  ? "visible"
                  : "hidden",
                borderRadius: "6px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.8rem",
                minWidth: "120px",
              }}
            >
              Reset Selects
            </Button>
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "0.85rem",
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              backgroundColor:
                theme.palette.mode === "light"
                  ? "rgba(0,0,0,0.04)"
                  : "rgba(255,255,255,0.08)",
              minWidth: "100px",
              textAlign: "center",
            }}
          >
            {aggregatedCounts.selected}/{aggregatedCounts.total} Selected
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "12px" }}>
        <GridInAccordion
          images={images}
          selectedImages={selectedImages}
          lockedSelectedImages={lockedSelectedImages}
          onImageSelect={onImageSelect}
          gridColumns={gridColumns}
          dimensions={updatedDimensions}
          itemSize={itemSize}
          selectsMade={selectsMade}
        />

        {/* {subfolders && typeof subfolders === 'object' &&
          Object.entries(subfolders)
            .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
            .map(([subfolderName, subfolderImages]) => (
            <EnhancedAccordion
              key={subfolderName}
              title={subfolderName}
              images={subfolderImages}
              selectedImages={selectedImages}
              lockedSelectedImages={lockedSelectedImages}
              onImageSelect={onImageSelect}
              gridColumns={gridColumns}
              dimensions={updatedDimensions}
              itemSize={itemSize}
              backgroundColor="#3a3a3a"
              onExpand={onExpand}
              containerWidth={containerWidth}
            />
          ))} */}
      </AccordionDetails>
    </Accordion>
  );
};

export default EnhancedAccordion;
